import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';

/**
 * ThreadCallContext
 *
 * Lets UI outside ThreadView (e.g. the chat panel header) start a voice call
 * on the currently displayed thread. ThreadView owns the actual call
 * machinery (isAudioMode + WebRTCVoiceChat) and registers a starter callback
 * here while a call is possible; the header renders a call button whenever a
 * starter is registered.
 */

interface ThreadCallContextValue {
  /** True when the visible ThreadView can start a call right now. */
  canStartCall: boolean;
  /** Start a call on the visible thread (no-op when unavailable). */
  startCall: () => void;
  /** ThreadView registers/unregisters its call starter here. */
  registerCallStarter: (starter: (() => void) | null) => void;
}

const ThreadCallContext = createContext<ThreadCallContextValue | undefined>(
  undefined,
);

export const ThreadCallProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const starterRef = useRef<(() => void) | null>(null);
  const [canStartCall, setCanStartCall] = useState(false);

  const registerCallStarter = useCallback((starter: (() => void) | null) => {
    starterRef.current = starter;
    setCanStartCall(!!starter);
  }, []);

  const startCall = useCallback(() => {
    starterRef.current?.();
  }, []);

  return (
    <ThreadCallContext.Provider
      value={{ canStartCall, startCall, registerCallStarter }}
    >
      {children}
    </ThreadCallContext.Provider>
  );
};

/** Optional variant — ThreadView renders in places without the provider. */
export const useThreadCallOptional = (): ThreadCallContextValue | undefined => {
  return useContext(ThreadCallContext);
};
